import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { PromptRun, PromptRunFailedSegmentsManifest } from '../services/api';
import {
  clampSegmentMarkerCount,
  getPromptRunActivityPhase,
  getPromptRunIndexId,
  PromptRunProgressSummary,
} from '../utils/promptRunProgress';
import {
  getOperationDisplayState,
  getOperationDetailLabel,
  getRunFailureCounts,
  isPromptRunTerminalStatus,
} from '../utils/promptRunFailures';
import { getPromptRunBillingDisplayState } from '../utils/promptRunBilling';
import { normalizePromptExecutionConfig, promptRunHasSelectionFilter } from '../features/workflows/config';
import { getEngineSegmentationLabel } from '../features/workflows/presentation';
import ProcessingProgressBar from './ProcessingProgressBar';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import './PromptRunProgressCardCompact.css';

interface PromptRunProgressCardCompactProps {
  run: PromptRun;
  summary: PromptRunProgressSummary;
  failureManifest?: PromptRunFailedSegmentsManifest | null;
  isExpanded?: boolean;
  onToggleExpand?: (runId: string) => void;
  onOpenWorkflow?: (run: PromptRun) => void;
  onOpenResults?: (run: PromptRun) => void;
  presentation?: 'admin' | 'workflow';
  children?: React.ReactNode;
}

const getStatusTone = (status?: string): 'success' | 'warning' | 'danger' | 'muted' => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'cancelled') return 'muted';
  if (normalized === 'failed' || normalized === 'completed_with_failures') return 'danger';
  if (normalized === 'processing' || normalized === 'pending' || normalized === 'queued' || normalized === 'stopping') return 'warning';
  return 'muted';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PromptRunProgressCardCompact: React.FC<PromptRunProgressCardCompactProps> = ({
  run,
  summary,
  failureManifest = null,
  isExpanded: controlledExpanded,
  onToggleExpand,
  onOpenWorkflow,
  onOpenResults,
  presentation = 'admin',
  children,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  // Keep visited workflow previews and their scroll/selection state on collapse.
  const hasExpandedRef = useRef(false);
  if (isExpanded) hasExpandedRef.current = true;

  const handleToggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand(run.run_id);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };
  const statusTone = getStatusTone(summary.status || run.status);
  const failureCounts = getRunFailureCounts(run, failureManifest);
  const billingState = getPromptRunBillingDisplayState(run);
  const indexId = getPromptRunIndexId(run);
  const hasSelectionFilter = promptRunHasSelectionFilter(run);
  const isWorkflow = presentation === 'workflow';
  const config = normalizePromptExecutionConfig(run);

  const extractState = getOperationDisplayState(
    true,
    run.field_extraction_succeeded,
    failureCounts.field_extraction,
    run.status
  );

  const useSegmentBuckets = summary.totalSegments > 0 && !summary.hasLiveMediaProgress;
  const successfulBucket = useSegmentBuckets ? summary.successfulSegments : summary.successfulMedia;
  const inProgressBucket = useSegmentBuckets ? summary.inProgressSegments : summary.inProgressMedia;
  const failedBucket = useSegmentBuckets ? summary.failedSegments : summary.failedMedia;
  const barTotal = useSegmentBuckets
    ? summary.totalSegments
    : summary.totalMedia;

  const toPercent = (value: number): number => {
    if (barTotal <= 0) return 0;
    return Math.max(0, Math.min(100, (value / barTotal) * 100));
  };

  const successWidth = toPercent(successfulBucket);
  const inProgressWidth = isPromptRunTerminalStatus(run.status) || run.status === 'pending' ? 0 : toPercent(inProgressBucket);
  const failedWidth = toPercent(failedBucket);

  // Segment markers for visualizing individual segments
  const markerCount = clampSegmentMarkerCount(summary.totalSegments, 120);

  const renderOperationIndicator = (
    label: string,
    state: ReturnType<typeof getOperationDisplayState>
  ) => (
    <span className={`op-indicator ${state.state}`} title={`${label}: ${state.text}`}>
      {label.charAt(0).toUpperCase()}
    </span>
  );

  const renderMediaCountIndicator = (
    label: string,
    count: number,
    description: string
  ) => (
    <span className="media-count-indicator" title={`${description}: ${count}`}>
      <span className="media-count-label">{label}</span>
      <span className="media-count-value">{count}</span>
    </span>
  );

  return (
    <article
      className={`run-card-compact ${isExpanded ? 'expanded' : ''}${isWorkflow ? ' run-card-workflow' : ''}`}
      data-tone={statusTone}
      data-run-id={run.run_id}
    >
      <button type="button" className="run-card-compact-header" onClick={handleToggleExpand} aria-expanded={isExpanded}>
        <div className="run-card-row">
          {/* Left: expand icon + name */}
          <div className="run-card-left">
            <span className="expand-icon">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <span className="run-name" title={summary.promptName}>
              {summary.promptName}
            </span>
            {isWorkflow && <span className="run-workflow-config">
              <span>{summary.totalMedia} media</span>
              <span>{getEngineSegmentationLabel(config)}</span>
              {hasSelectionFilter && <span>Filtered</span>}
              <span>Visual search {config.enable_image_embedding ? 'on' : 'off'}</span>
            </span>}
          </div>

          {/* Center: long segmented progress bar */}
          <div className="run-progress-bar-container">
            {isInProgressStatus(run.status) ? (
              <ProcessingProgressBar status={run.status} phase={getPromptRunActivityPhase(run)}
                lastWorkerActivityAt={run.liveness?.last_heartbeat_at}

                completed={successfulBucket + failedBucket + (useSegmentBuckets ? 0 : summary.cancelledMedia)}
                total={barTotal} failed={failedBucket} unit={useSegmentBuckets ? 'segments' : 'media'} />
            ) : <div className="run-progress-bar" role="progressbar" aria-label="Execution progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(successWidth + failedWidth)} aria-valuetext={`${successfulBucket + failedBucket} of ${barTotal} ${useSegmentBuckets ? 'segments' : 'media'} finished`}>
              <div className="progress-segment success" style={{ width: `${successWidth}%` }} />
              <div className="progress-segment in-progress" style={{ width: `${inProgressWidth}%` }} />
              <div className="progress-segment failed" style={{ width: `${failedWidth}%` }} />
              {useSegmentBuckets && markerCount > 1 && (
                <div className="progress-markers" aria-hidden="true">
                  {Array.from({ length: markerCount - 1 }).map((_, index) => (
                    <span
                      key={`${run.run_id}-marker-${index}`}
                      className="progress-marker"
                      style={{ left: `${((index + 1) / markerCount) * 100}%` }}
                    />
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Right: fixed metadata slots keep each run aligned. */}
          <div className="run-card-right">
            {!isWorkflow && <span className="run-filter">
              {hasSelectionFilter ? 'Filtered' : 'Unfiltered'}
            </span>}
            <span className="run-time">{formatDateTime(summary.executedAt || run.executed_at)}</span>
            {!isWorkflow && <><div
              className="media-count-indicators"
              aria-label={`${summary.totalVideos} videos, ${summary.totalAudios} audio files, ${summary.totalImages} images`}
            >
              {renderMediaCountIndicator('Video', summary.totalVideos, 'Videos')}
              {renderMediaCountIndicator('Audio', summary.totalAudios, 'Audio files')}
              {renderMediaCountIndicator('Image', summary.totalImages, 'Images')}
            </div>
            <div className="op-indicators">
              {renderOperationIndicator('extract', extractState)}
            </div>
            {billingState.visible && (
              <span className={`run-billing-badge ${billingState.tone}`} title={billingState.detail}>
                {billingState.label}
              </span>
            )}
            </>}
            <span className={`run-status-badge ${statusTone}`} title={(summary.status || run.status).replace(/_/g, ' ')}>
              {(summary.status || run.status) === 'pending' ? 'Queued' : (summary.status || run.status).replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </button>

      {(isExpanded || (isWorkflow && hasExpandedRef.current)) && (
        <div className="run-card-compact-details" hidden={!isExpanded}>
          {isWorkflow ? children : <>
          {/* Row 1: Core identifiers */}
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Run ID</span>
              <span className="detail-value mono" title={run.run_id}>{run.run_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Extraction Engine ID</span>
              <span className="detail-value mono" title={run.prompt_id}>{run.prompt_id || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Extraction Engine Name</span>
              <span className="detail-value">{run.prompt_name || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Extraction Engine Type</span>
              <span className="detail-value">{run.prompt_type || 'custom'}</span>
            </div>
          </div>

          {/* Row 2: Status and processing */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">{run.status}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Executed At</span>
              <span className="detail-value">{run.executed_at ? new Date(run.executed_at).toLocaleString() : 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Executed By</span>
              <span className="detail-value">{run.executed_by || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Billing</span>
              <span className={`detail-value billing-detail ${billingState.tone}`}>
                {billingState.visible ? billingState.label : 'N/A'}
              </span>
            </div>
          </div>

          {(run.reuse_summary || hasSelectionFilter) && (
            <div className="details-grid" style={{ marginTop: '10px' }}>
              {run.reuse_summary && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Reused Media</span>
                    <span className="detail-value">{run.reuse_summary.reused_media_count}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Fresh Media</span>
                    <span className="detail-value">{run.reuse_summary.fresh_media_count}</span>
                  </div>
                </>
              )}
              {hasSelectionFilter && run.selection_summary && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Selection</span>
                    <span className="detail-value">{run.selection_summary?.status || 'pending'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Selected Media</span>
                    <span className="detail-value">{run.selection_summary?.selected_media_count ?? 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Video-level Matches</span>
                    <span className="detail-value">{run.selection_summary?.selected_video_count ?? 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Segment Matches</span>
                    <span className="detail-value">{run.selection_summary?.selected_segment_count ?? 0}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {billingState.visible && billingState.detail && (
            <div className={`billing-detail-row ${billingState.tone}`}>
              {billingState.detail}
            </div>
          )}

          {/* Row 3: Source info */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Source</span>
              <span className="detail-value">
                {run.run_context?.is_playground ? 'Playground' : (indexId ? 'Index' : 'N/A')}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Index ID</span>
              <span className="detail-value mono">{indexId || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Is Playground</span>
              <span className="detail-value">{run.run_context?.is_playground ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {/* Row 4: More settings */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Image Embedding</span>
              <span className="detail-value">{run.enable_image_embedding !== false ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Field Extraction</span>
              <span className="detail-value">{getOperationDetailLabel(extractState)}</span>
            </div>
          </div>

          {/* Row 5: Video counts */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Total Videos</span>
              <span className="detail-value">{run.total_videos ?? summary.totalVideos}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Completed Videos</span>
              <span className="detail-value count-success">{run.completed_videos ?? summary.successfulVideos}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Failed Videos</span>
              <span className="detail-value count-failed">{run.failed_videos ?? summary.failedVideos}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cancelled Videos</span>
              <span className="detail-value">{run.cancelled_videos ?? summary.cancelledVideos}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">In Progress</span>
              <span className="detail-value count-progress">{summary.inProgressVideos}</span>
            </div>
          </div>

          {/* Row 6: Segment counts */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Total Segments</span>
              <span className="detail-value">{run.total_segments ?? summary.totalSegments}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Completed Segments</span>
              <span className="detail-value count-success">{run.completed_segments ?? summary.successfulSegments}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Failed Segments</span>
              <span className="detail-value count-failed">{summary.failedSegments}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Pending Segments</span>
              <span className="detail-value count-progress">{summary.pendingSegments}</span>
            </div>
          </div>

          {/* Row 7: Media counts */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Total Audios</span>
              <span className="detail-value">{run.total_audios ?? summary.totalAudios ?? 0}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Completed Audios</span>
              <span className="detail-value count-success">{run.completed_audios ?? summary.successfulAudios ?? 0}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cancelled Audios</span>
              <span className="detail-value">{run.cancelled_audios ?? summary.cancelledAudios ?? 0}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Total Images</span>
              <span className="detail-value">{run.total_images ?? summary.totalImages ?? 0}</span>
            </div>
          </div>

          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Completed Images</span>
              <span className="detail-value count-success">{run.completed_images ?? summary.successfulImages ?? 0}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cancelled Images</span>
              <span className="detail-value">{run.cancelled_images ?? summary.cancelledImages ?? 0}</span>
            </div>
          </div>

          {/* Operations row */}
          <div className="operations-row">
            <span className="detail-label">Operations</span>
            <div className="operations-list">
              <span className={`operation-tag ${extractState.state === 'ok' ? 'enabled' : extractState.state === 'failed' ? 'failed' : 'enabled'}`}>
                Extraction {extractState.state === 'failed' && `(${failureCounts.field_extraction} failed)`}
              </span>
              <span className={`operation-tag ${run.enable_image_embedding !== false ? 'enabled' : 'disabled'}`}>
                Image Embedding
              </span>
            </div>
          </div>

          {/* Video IDs if available */}
          {run.run_context?.video_ids && Array.isArray(run.run_context.video_ids) && run.run_context.video_ids.length > 0 && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Target Video IDs ({run.run_context.video_ids.length})</span>
              <div className="detail-value mono id-list">
                {run.run_context.video_ids.map((id: string, idx: number) => (
                  <span key={idx} className="id-item">{id}</span>
                ))}
              </div>
            </div>
          )}

          {/* Run Context as JSON */}
          {run.run_context && Object.keys(run.run_context).length > 0 && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Run Context (Raw)</span>
              <pre className="raw-json">{JSON.stringify(run.run_context, null, 2)}</pre>
            </div>
          )}

          </>}
          {onOpenWorkflow && indexId && (
            <button
              className="retry-btn"
              onClick={(event) => {
                event.stopPropagation();
                onOpenWorkflow(run);
              }}
            >
              Open workflow <ExternalLink size={12} />
            </button>
          )}

          {onOpenResults && indexId && isPromptRunTerminalStatus(run.status) && (
            <button
              className="retry-btn"
              onClick={(event) => {
                event.stopPropagation();
                onOpenResults(run);
              }}
            >
              View results <ExternalLink size={12} />
            </button>
          )}

          {run.error_message && (
            <div className="error-message" title={run.error_message}>
              {run.error_message}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default PromptRunProgressCardCompact;
